import { Timestamp } from "firebase/firestore";
import { MaskHelper, SearchHelper } from "../../utils/helpers";
import { toISODate } from "../../utils/date";

/**
 * Formata um nome deixando a primeira letra de cada palavra maiúscula e o resto minúsculo,
 * ignorando preposições comuns.
 */
const formatName = (name) => {
    if (!name) return "";
    return name.trim().split(/\s+/).map((word, index) => {
        const lower = word.toLowerCase();
        // preposições comuns continuam minúsculas, exceto se for a primeira palavra
        if (index > 0 && ['da', 'de', 'do', 'das', 'dos', 'e'].includes(lower)) {
            return lower;
        }
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }).join(' ');
};

/**
 * Formulário → Firestore
 * Converte dados flat do formulário React para a estrutura do Firestore.
 *
 * Contrato:
 * - address no formulário = string (logradouro / street)
 * - address no Firestore = { zipCode, street, number, complement, neighborhood, city, state, country }
 * - gender / maritalStatus / status / membershipStatus = string simples (ex: "M", "single", "active")
 */
export const memberToFirestore = (data, tenantData, authUser) => {
    const idTenant = typeof tenantData === 'string' ? tenantData : tenantData?.id;
    const tenantName = typeof tenantData === 'string' ? '' : (tenantData?.tenantName || tenantData?.name || '');
    if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

    const firstName = formatName(data.firstName || "");
    const lastName = formatName(data.lastName || "");
    const fullName = `${firstName} ${lastName}`.trim();
    const searchName = SearchHelper.normalize(fullName);

    const payload = {
        idTenant,
        tenantName,
        firstName,
        lastName,
        fullName,
        searchName,

        document: MaskHelper.unmask(data.document) || null,
        documentId: data.documentId?.trim() || "",
        maritalStatus: data.maritalStatus || null,
        gender: data.gender || null,
        birthDate: typeof data.birthDate === 'string' ? data.birthDate :
            (data.birthDate instanceof Date ? toISODate(data.birthDate) : null),
        photoUrl: data.photoUrl || null,

        email: data.email?.toLowerCase().trim() || "",
        phone: MaskHelper.unmask(data.phone) || null,
        address: {
            zipCode: MaskHelper.unmask(data.zipCode) || null,
            street: data.address?.trim() || "",
            number: data.number?.trim() || "",
            complement: data.complement?.trim() || "",
            neighborhood: data.neighborhood?.trim() || "",
            city: data.city?.trim() || "",
            state: data.state?.trim() || "",
            country: data.country || "Brasil"
        },

        emergencyContact: {
            name: data.emergencyContact?.name?.trim() || "",
            phone: MaskHelper.unmask(data.emergencyContact?.phone) || null,
            email: data.emergencyContact?.email?.toLowerCase().trim() || "",
            relationship: data.emergencyContact?.relationship || null
        },

        healthInfo: {
            bloodType: data.healthInfo?.bloodType || null,
            allergies: data.healthInfo?.allergies?.trim() || "",
            medications: data.healthInfo?.medications?.trim() || "",
            medicalConditions: data.healthInfo?.medicalConditions?.trim() || "",
            observations: data.healthInfo?.observations?.trim() || ""
        },

        status: (data.status && typeof data.status === 'object' ? data.status.id || data.status.value : data.status) || "lead",
        membershipStatus: (data.membershipStatus && typeof data.membershipStatus === 'object' ? data.membershipStatus.id || data.membershipStatus.value : data.membershipStatus) || "none",
        tag: (data.tag && typeof data.tag === 'object' ? data.tag.id || data.tag.value || data.tag.label : data.tag) || "new",
        penalized: data.penalized || false,
        xpTotal: data.xpTotal || 0,
        accessBlocked: data.accessBlocked || false,
        blockedReason: data.blockedReason || null,        

        // Tracking & Cíclo de Vida
        originId: data.originId || null,
        firstPurchaseDate: data.firstPurchaseDate ? (data.firstPurchaseDate instanceof Date ? Timestamp.fromDate(data.firstPurchaseDate) : data.firstPurchaseDate) : null,
        churnDate: data.churnDate ? (data.churnDate instanceof Date ? Timestamp.fromDate(data.churnDate) : data.churnDate) : null,
        churnReason: data.churnReason || null,

        // Responsáveis
        consultant: data.consultant ? {
            uid: data.consultant.uid || data.consultant.id || data.consultant.value || null,
            name: data.consultant.name || data.consultant.label || data.consultant.fullName || "",
            email: data.consultant.email || null,
            phone: data.consultant.phone || data.consultant.phoneNumber || null,
            photoUrl: data.consultant.photoUrl || data.consultant.photoURL || null
        } : (data.id ? null : {
            uid: authUser?.uid || null,
            name: authUser?.displayName || authUser?.fullName || "",
            email: authUser?.email || null,
            phone: authUser?.phone || authUser?.phoneNumber || null,
            photoUrl: authUser?.photoURL || authUser?.photoUrl || null
        }),
        teacher: data.id ? (data.teacher || null) : null,

        createdAt: data.id ? (data.createdAt || Timestamp.now()) : Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: data.id ? data.createdBy : {
            uid: authUser?.uid || "system",
            name: authUser?.displayName || authUser?.fullName || "Sistema",
            photoUrl: authUser?.photoURL || authUser?.photoUrl || null
        },
        updatedBy: {
            uid: authUser?.uid || "system",
            name: authUser?.displayName || authUser?.fullName || "Sistema",
            photoUrl: authUser?.photoURL || authUser?.photoUrl || null
        }
    };

    Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
    return payload;
};

/**
 * Firestore → React (para um único documento snapshot)
 * Converte a estrutura do Firestore para dados flat do formulário React.
 *
 * Transformações:
 * - address { zipCode, street, number, ... } → campos root: zipCode, address (=street), number, ...
 * - Timestamps → Date ou ISO string
 */
export const memberFromFirestore = (snapshot) => {
    if (!snapshot.exists()) return null;
    const data = snapshot.data();

    return memberDataFromFirestore(snapshot.id, data);
};

/**
 * Firestore data (raw) → React (formato flat do formulário)
 * Versão que aceita id + data diretamente, para uso no MemberService.
 */
export const memberDataFromFirestore = (id, data) => {
    if (!data) return null;

    return {
        id,
        ...data,

        // Flatten: address object → root-level fields
        zipCode: data.address?.zipCode,
        address: data.address?.street,
        number: data.address?.number,
        complement: data.address?.complement,
        neighborhood: data.address?.neighborhood,
        city: data.address?.city,
        state: data.address?.state,
        country: data.address?.country,

        // Garantir que status, membershipStatus e tag sejam strings, mesmo se vierem como objeto do BD (caso de legados/erros)
        status: data.status && typeof data.status === 'object' ? data.status.id || data.status.value : (data.status || 'lead'),
        membershipStatus: data.membershipStatus && typeof data.membershipStatus === 'object' ? data.membershipStatus.id || data.membershipStatus.value : (data.membershipStatus || 'none'),
        tag: data.tag && typeof data.tag === 'object' ? data.tag.id || data.tag.value || data.tag.label : (data.tag || 'new'),

        // Timestamps → Date / ISO string
        birthDate: data.birthDate?.toDate ? toISODate(data.birthDate.toDate()) : data.birthDate,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
        
        // Tracking & Ciclo de Vida
        originId: data.originId || null,
        firstPurchaseDate: data.firstPurchaseDate?.toDate ? data.firstPurchaseDate.toDate() : data.firstPurchaseDate,
        churnDate: data.churnDate?.toDate ? data.churnDate.toDate() : data.churnDate,
        churnReason: data.churnReason || null,
    };
};
